/**
 * Lazy-load Pannellum (~50KB JS + small CSS) on first VR open instead
 * of paying the cost on every page load. Promise is cached so concurrent
 * opens don't fetch twice.
 *
 * CDN: jsdelivr → pannellum 2.5.6 (last stable as of 2026-05).
 */

const PANNELLUM_VERSION = '2.5.6';
const CDN = `https://cdn.jsdelivr.net/npm/pannellum@${PANNELLUM_VERSION}/build`;

/** Minimal subset of Pannellum's runtime API we actually use. */
export interface PannellumViewer {
  loadScene(sceneId: string): void;
  destroy(): void;
  toggleFullscreen(): void;
  setHfov(hfov: number, animated?: boolean): void;
  startAutoRotate(speed?: number): void;
  stopAutoRotate(): void;
}

export interface PannellumScene {
  type: 'equirectangular';
  panorama: string;
  hfov?: number;
  yaw?: number;
  pitch?: number;
  autoLoad?: boolean;
}

export interface PannellumConfig {
  default: {
    firstScene: string;
    autoLoad?: boolean;
    sceneFadeDuration?: number;
    showControls?: boolean;
  };
  scenes: Record<string, PannellumScene>;
  /**
   * Sets the `crossOrigin` attribute on every Image Pannellum creates.
   * "anonymous" is required for our R2-hosted panoramas — without it,
   * the WebGL texture upload from a cross-origin image fails (the
   * canvas would be CORS-tainted) and the viewer falls back to the
   * "could not be accessed" overlay.
   */
  crossOrigin?: 'anonymous' | 'use-credentials' | '';
}

interface PannellumNamespace {
  viewer(containerId: string | HTMLElement, config: PannellumConfig): PannellumViewer;
}

declare global {
  interface Window {
    pannellum?: PannellumNamespace;
  }
}

let loadingPromise: Promise<PannellumNamespace> | null = null;

export function loadPannellum(): Promise<PannellumNamespace> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Pannellum requires a browser'));
  }
  if (window.pannellum) return Promise.resolve(window.pannellum);
  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise<PannellumNamespace>((resolve, reject) => {
    // CSS — append once. id lookup avoids duplicates.
    if (!document.getElementById('pannellum-css')) {
      const link = document.createElement('link');
      link.id = 'pannellum-css';
      link.rel = 'stylesheet';
      link.href = `${CDN}/pannellum.css`;
      document.head.appendChild(link);
    }

    // JS
    const existing = document.getElementById('pannellum-js') as HTMLScriptElement | null;
    if (existing && window.pannellum) {
      resolve(window.pannellum);
      return;
    }
    const script = existing ?? document.createElement('script');
    script.id = 'pannellum-js';
    script.src = `${CDN}/pannellum.js`;
    script.async = true;
    script.onload = () => {
      if (window.pannellum) resolve(window.pannellum);
      else reject(new Error('pannellum loaded but window.pannellum is undefined'));
    };
    script.onerror = () => {
      loadingPromise = null;
      reject(new Error(`Failed to load Pannellum from ${CDN}`));
    };
    if (!existing) document.body.appendChild(script);
  });

  return loadingPromise;
}
